import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons";
import { usePaintedImageRect } from "../hooks/usePaintedImageRect";
import { computeDragSize, stepSize } from "../functions/dragResize";
import {
  claimResizeHandlePulse,
  RESIZE_HANDLE_PULSE_MS,
} from "../functions/resizeHandlePulse";

export interface LogoResizeHandleProps {
  containerRef: React.RefObject<HTMLDivElement>;
  logoRef: React.RefObject<HTMLImageElement>;
  // Size fields (the form) - what the drag previews into.
  widthMm: number;
  heightMm: number;
  // Saved size (the logo) - a release on this size makes no call.
  savedWidthMm: number;
  savedHeightMm: number;
  transferMethodSize?: string | null;
  disabled?: boolean;
  onPreview: (widthMm: number, heightMm: number) => void;
  onCommit: (widthMm: number, heightMm: number) => void;
  onCancel: (widthMm: number, heightMm: number) => void;
  onDragStateChange: (dragging: boolean) => void;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  startWidthMm: number;
  startHeightMm: number;
  paintedWidthPx: number;
  paintedHeightPx: number;
  lastWidthMm: number;
  lastHeightMm: number;
}

// Hit area (comfortable on touch); the visible round button is 24px inside
// it, centred on the painted corner of the logo.
const HANDLE_PX = 32;
const BUTTON_PX = 24;

/**
 * Flag REACT_APP_LOGO_DRAG_RESIZE: a handle on the bottom-right corner of
 * the painted logo. Dragging previews the size into the form (fields, ruler
 * and canvas scale follow), releasing commits ONCE through onCommit - never
 * when the size is unchanged or the drag was cancelled (Esc / pointercancel
 * -> onCancel with the start size). Keyboard when focused: arrows nudge 1 mm
 * (Shift 10) into the form, Enter commits, Esc returns to the saved size.
 * Renders nothing while disabled or before the bitmap has been measured.
 *
 * Look: a round white button with a diagonal double arrow, a tooltip with
 * the handle's name, hover/focus highlight, and - once per browser session
 * (claimResizeHandlePulse) - three expanding rings so a reseller who has
 * never seen it notices it. Neither the tooltip nor the rings take part in
 * the pointer/keyboard logic above. The tooltip hides while a drag runs and
 * stays hidden after it until the pointer leaves or focus moves; touch never
 * opens it (a hold on this handle is a drag, not a long-press).
 */
export function LogoResizeHandle(props: LogoResizeHandleProps) {
  const {
    containerRef,
    logoRef,
    widthMm,
    heightMm,
    savedWidthMm,
    savedHeightMm,
    transferMethodSize,
    disabled = false,
  } = props;
  const { t } = useTranslation();
  const rect = usePaintedImageRect(containerRef, logoRef);
  const handleRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const frameRef = useRef<number | null>(null);
  const pendingMoveRef = useRef<{ x: number; y: number } | null>(null);
  // Aspect locked per gesture (focus / pointer-down), as LogoResize does per
  // typing session - recomputing it from each rounded result would drift.
  const aspectRef = useRef(1);
  const [dragging, setDragging] = useState(false);
  // Tooltip is controlled so it can stay away while a drag runs (the mm
  // badge takes over) and after one, until the pointer leaves or focus
  // moves: MUI only reports onClose while open, so a drag would otherwise
  // leave it "open" and pop it back over the logo on release (and its 700 ms
  // touch hold would open it mid-drag). Hover/focus behave as MUI's default.
  const [tipOpen, setTipOpen] = useState(false);
  const tipSuppressedRef = useRef(false);
  const [pulse, setPulse] = useState(false);
  const pulseClaimedRef = useRef(false);
  // Latest props for the window listener and the animation frame.
  const propsRef = useRef(props);
  propsRef.current = props;

  // A rect without extent means the logo has no layout yet (hidden panel,
  // display:none, not laid out): no handle, and above all no pulse claimed
  // where nobody can see it - the session's one pulse would be gone.
  const hasLayout = !!rect && rect.width > 0 && rect.height > 0;
  const visible = !disabled && hasLayout && widthMm > 0 && heightMm > 0;

  // First-time pulse: claimed when the handle first becomes visible in this
  // mount (never for a handle that stays hidden), and only once per session.
  useEffect(() => {
    if (!visible || pulseClaimedRef.current) return;
    pulseClaimedRef.current = true;
    if (claimResizeHandlePulse()) setPulse(true);
  }, [visible]);

  // The rings are finite in CSS; the element itself goes after the run.
  useEffect(() => {
    if (!pulse) return;
    const timer = setTimeout(() => setPulse(false), RESIZE_HANDLE_PULSE_MS);
    return () => clearTimeout(timer);
  }, [pulse]);

  function beginGesture() {
    aspectRef.current = widthMm > 0 && heightMm > 0 ? widthMm / heightMm : 1;
  }

  // MUI raises onOpen for hover ("mouseover"), keyboard focus ("focus") and,
  // after its 700 ms hold, "touchstart". Only the first two ever open it,
  // and never during a drag or right after one (see tipSuppressedRef).
  function openTooltip(e: React.SyntheticEvent) {
    if (dragRef.current || tipSuppressedRef.current) return;
    if (e.type === "touchstart") return;
    setTipOpen(true);
  }

  // The pointer left the handle / focus moved on: hover and focus may open
  // the tooltip again.
  function releaseTooltip() {
    tipSuppressedRef.current = false;
  }

  function applyMove() {
    frameRef.current = null;
    const drag = dragRef.current;
    const move = pendingMoveRef.current;
    if (!drag || !move) return;
    const next = computeDragSize({
      startWidthMm: drag.startWidthMm,
      startHeightMm: drag.startHeightMm,
      paintedWidthPx: drag.paintedWidthPx,
      paintedHeightPx: drag.paintedHeightPx,
      dx: move.x - drag.startX,
      dy: move.y - drag.startY,
      methodSize: propsRef.current.transferMethodSize,
    });
    if (
      next.widthMm === drag.lastWidthMm &&
      next.heightMm === drag.lastHeightMm
    ) {
      return;
    }
    drag.lastWidthMm = next.widthMm;
    drag.lastHeightMm = next.heightMm;
    propsRef.current.onPreview(next.widthMm, next.heightMm);
  }

  function endDrag(): DragState | null {
    const drag = dragRef.current;
    if (!drag) return null;
    dragRef.current = null;
    pendingMoveRef.current = null;
    if (frameRef.current !== null) {
      if (typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = null;
    }
    const el = handleRef.current;
    if (el && typeof el.releasePointerCapture === "function") {
      try {
        el.releasePointerCapture(drag.pointerId);
      } catch {
        // capture already gone (pointercancel) - nothing to release
      }
    }
    tipSuppressedRef.current = true;
    setTipOpen(false);
    setDragging(false);
    propsRef.current.onDragStateChange(false);
    return drag;
  }

  function cancelDrag() {
    const drag = endDrag();
    if (!drag) return;
    propsRef.current.onCancel(drag.startWidthMm, drag.startHeightMm);
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (disabled || dragRef.current || !rect) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // No text selection / native drag while resizing; focus by hand so the
    // keyboard keeps working afterwards.
    e.preventDefault();
    e.currentTarget.focus();
    beginGesture();
    if (typeof e.currentTarget.setPointerCapture === "function") {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // pointer already gone - the move/up listeners still cover the element
      }
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startWidthMm: widthMm,
      startHeightMm: heightMm,
      paintedWidthPx: rect.width,
      paintedHeightPx: rect.height,
      lastWidthMm: widthMm,
      lastHeightMm: heightMm,
    };
    setDragging(true);
    props.onDragStateChange(true);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    pendingMoveRef.current = { x: e.clientX, y: e.clientY };
    if (frameRef.current !== null) return;
    if (typeof requestAnimationFrame === "function") {
      frameRef.current = requestAnimationFrame(applyMove);
    } else {
      applyMove();
    }
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    // The release position counts even if its frame has not run yet.
    if (pendingMoveRef.current) applyMove();
    const finished = endDrag();
    if (!finished) return;
    const { lastWidthMm: w, lastHeightMm: h } = finished;
    const unchanged =
      w === finished.startWidthMm && h === finished.startHeightMm;
    const isSaved =
      w === propsRef.current.savedWidthMm &&
      h === propsRef.current.savedHeightMm;
    if (unchanged || isSaved) return;
    propsRef.current.onCommit(w, h);
  }

  function handlePointerCancel(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    cancelDrag();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled || dragRef.current) return;
    let delta = 0;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        delta = e.shiftKey ? 10 : 1;
        break;
      case "ArrowLeft":
      case "ArrowDown":
        delta = e.shiftKey ? -10 : -1;
        break;
      case "Enter":
        e.preventDefault();
        if (widthMm !== savedWidthMm || heightMm !== savedHeightMm) {
          props.onCommit(widthMm, heightMm);
        }
        return;
      case "Escape":
        if (widthMm !== savedWidthMm || heightMm !== savedHeightMm) {
          e.preventDefault();
          props.onCancel(savedWidthMm, savedHeightMm);
        }
        return;
      default:
        return;
    }
    e.preventDefault();
    const next = stepSize(
      { widthMm, heightMm },
      delta,
      aspectRef.current,
      transferMethodSize
    );
    if (next.widthMm !== widthMm || next.heightMm !== heightMm) {
      props.onPreview(next.widthMm, next.heightMm);
    }
  }

  // Esc cancels a running drag wherever the keyboard focus is.
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      cancelDrag();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Unmounted mid-drag (the design editor can swap the logo under the tool):
  // release the drag state so the canvas transition comes back.
  useEffect(() => {
    return () => {
      if (dragRef.current) {
        dragRef.current = null;
        propsRef.current.onDragStateChange(false);
      }
    };
  }, []);

  if (!visible || !rect) return null;

  const cornerX = rect.left + rect.width;
  const cornerY = rect.top + rect.height;
  const sizeLabel = t("logoEditorComponents.dragResizeSizeLabel", {
    width: widthMm,
    height: heightMm,
  });

  return (
    <>
      {dragging && (
        <div
          className={
            "absolute z-30 pointer-events-none rounded bg-gray-800/90 text-white " +
            "text-[11px] leading-tight px-2 py-1 whitespace-nowrap shadow"
          }
          style={{
            left: cornerX - HANDLE_PX / 2 - 4,
            top: cornerY - HANDLE_PX / 2 - 4,
            transform: "translate(-100%, -100%)",
          }}
          data-testid="logo-resize-badge"
        >
          <div className={"font-semibold"}>{sizeLabel}</div>
          <div className={"opacity-80"}>
            {t("logoEditorComponents.dragResizeHint")}
          </div>
        </div>
      )}
      <Tooltip
        title={t("logoEditorComponents.dragResizeHandle")}
        placement="top"
        arrow
        disableInteractive
        open={tipOpen && !dragging}
        onOpen={openTooltip}
        onClose={() => setTipOpen(false)}
      >
        <div
          ref={handleRef}
          role="slider"
          tabIndex={0}
          aria-label={t("logoEditorComponents.dragResizeHandle")}
          aria-valuemin={1}
          aria-valuenow={widthMm}
          aria-valuetext={sizeLabel}
          aria-orientation="horizontal"
          data-testid="logo-resize-handle"
          className={
            "absolute z-30 flex items-center justify-center cursor-nwse-resize " +
            "touch-none select-none outline-none group"
          }
          style={{
            left: cornerX - HANDLE_PX / 2,
            top: cornerY - HANDLE_PX / 2,
            width: HANDLE_PX,
            height: HANDLE_PX,
          }}
          onFocus={beginGesture}
          onBlur={releaseTooltip}
          onMouseLeave={releaseTooltip}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKeyDown}
          onContextMenu={(e) => e.preventDefault()}
        >
          {pulse && (
            <span
              aria-hidden="true"
              data-testid="logo-resize-pulse"
              className={
                "absolute rounded-full bg-blue-500/50 opacity-0 pointer-events-none " +
                "animate-resizeHandlePing motion-reduce:hidden"
              }
              style={{
                left: (HANDLE_PX - BUTTON_PX) / 2,
                top: (HANDLE_PX - BUTTON_PX) / 2,
                width: BUTTON_PX,
                height: BUTTON_PX,
              }}
            />
          )}
          <span
            className={
              "relative flex items-center justify-center rounded-full bg-white " +
              "border-2 shadow-md text-gray-700 transition-transform duration-100 " +
              "motion-reduce:transition-none " +
              "group-hover:border-blue-600 group-hover:text-blue-600 group-hover:scale-110 " +
              "group-focus-visible:ring-2 group-focus-visible:ring-blue-500 " +
              "group-focus-visible:ring-offset-1" +
              (dragging
                ? " border-blue-600 text-blue-600 bg-blue-50 scale-110"
                : " border-gray-700")
            }
            style={{ width: BUTTON_PX, height: BUTTON_PX }}
          >
            <FontAwesomeIcon
              icon={faUpRightAndDownLeftFromCenter}
              rotation={90}
              className={"text-[11px]"}
              data-testid="logo-resize-icon"
            />
          </span>
        </div>
      </Tooltip>
    </>
  );
}
